import { useRef } from "react";
import { API_BASE_URL } from "@/mocks/adminData";

interface UseImageUploadOptions {
  /** 上传到服务器的接口路径（相对于 API_BASE_URL），不传则只用 base64 */
  uploadPath?: string;
  /** 认证 token，上传到服务器时需要 */
  token?: string;
  /** 服务器上传时的 form field 名称，默认 image */
  fieldName?: string;
}

/**
 * 图片上传 hook
 * - 有 uploadPath + token：先上传到服务器，返回服务器 URL
 * - 否则：读取为 base64 DataURL（本地预览）
 */
export function useImageUpload(
  onResult: (url: string) => void,
  options: UseImageUploadOptions = {}
) {
  const { uploadPath, token, fieldName = "image" } = options;
  const inputRef = useRef<HTMLInputElement>(null);

  const trigger = () => inputRef.current?.click();

  const handleFile = async (file: File) => {
    // 有服务器上传配置时，优先上传到服务器
    if (uploadPath && token) {
      try {
        const formData = new FormData();
        formData.append(fieldName, file);
        const res = await fetch(`${API_BASE_URL}/${uploadPath}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (data.code === 200 && data.data?.url) {
          onResult(data.data.url);
          return;
        }
        // 服务器上传失败，降级为 base64
        console.warn("服务器上传失败，降级为本地预览:", data.msg);
      } catch (err) {
        console.warn("服务器上传异常，降级为本地预览:", err);
      }
    }

    // 降级：读取为 base64
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      if (result) onResult(result);
    };
    reader.readAsDataURL(file);
  };

  const el = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        handleFile(file);
        e.target.value = "";
      }}
    />
  );

  return { trigger, el };
}